// ─────────────────────────────────────────────
//  Google Sheet Import config panel (facility-specific) — Setup tab.
//
//  Lets an admin OR the AIC edit the per-facility sheetImportConfig
//  (title prefix, optional fixed sheet id, optional tab override) without a
//  code deploy. Extracted to its own file (not setupPage.facility.js,
//  already 435 lines) per the 500-line cap, exactly like SurgeonsPanel/
//  StaffPanel live in setupPage.roster.js.
//
//  Access: unlike FacilityInfoPanel (admin-only), this panel is editable by
//  admin OR AIC — the Firestore rule for this field is field-scoped to match
//  (Plan 03), mirroring the existing `rooms` field-scoping pattern.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { collection, getDocs, limit, orderBy, query } from "../firebase/sdk";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateFacility } from "../firebase/firestore";
import { useFacility } from "../hooks/useFacility";
import { facilityLabel } from "../domain/facilities";
import { compileRoomFamily } from "../domain/sheetRoomFamily";
import { usesFormattingAic } from "../domain/sheetAicMarker";
import { SheetEngineSection } from "./setupPage.sheetEngine";
import { DEFAULT_FACILITY_ID } from "../config/roles";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

// Allowlist for title prefix / sheet id / tab: letters, numbers, spaces, and
// - . _ & ( ) — Security V5. Blocks save (no Firestore write) on violation.
const ALLOWED_CHARS_RE = /^[A-Za-z0-9 \-._&()]*$/;
import ImportRunSummary from "../components/ImportRunSummary";

const MAX_FIELD_LEN = 200;

const FIELDS = [
  {
    key: "titlePrefix",
    label: "Title prefix",
    envVar: "REACT_APP_ATC_SHEET_TITLE",
    envFallback: "Prelim Anes Assignments",
    placeholder: "Prelim Anes Assignments",
    blankHelper: null,
  },
  {
    key: "sheetId",
    label: "Fixed sheet ID (optional)",
    envVar: "REACT_APP_ATC_SHEET_ID",
    envFallback: "(auto-detect)",
    placeholder: "(auto-detect)",
    blankHelper: "Leave blank to auto-detect today's file by title (recommended).",
  },
  {
    key: "tab",
    label: "Tab override (optional)",
    envVar: "REACT_APP_ATC_SHEET_TAB",
    envFallback: "(auto-detect)",
    placeholder: "(auto-detect)",
    blankHelper: "Leave blank to auto-detect the filled Assignment Sheet tab (recommended).",
  },
];

function envFallbackValue(envVar, fallback) {
  return process.env[envVar] || fallback;
}

// Editor for the GLOBAL import's own sheet settings (all-facilities button on
// the Daily List). Rides on facilities/default so it needs no new collection
// or rules; distinct from every facility's own sheetImportConfig above.
function GlobalConfigSection() {
  const toast = useToast();
  const { facility: defaultFacility } = useFacility(DEFAULT_FACILITY_ID);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  // Latest automated (midnight) run — written by the Cloud Function to
  // import_runs; admin-readable so a broken night is visible right here.
  const [lastRun, setLastRun] = useState(null);
  useEffect(() => {
    getDocs(query(collection(db, "import_runs"), orderBy("ranAt", "desc"), limit(1)))
      .then((snap) => setLastRun(snap.docs[0] ? snap.docs[0].data() : null))
      .catch(() => setLastRun(null)); // collection absent until the first run
  }, []);

  useEffect(() => {
    const cfg = defaultFacility?.globalSheetImportConfig || {};
    setForm({ titlePrefix: cfg.titlePrefix || "", sheetId: cfg.sheetId || "", tab: cfg.tab || "" });
  }, [defaultFacility]);

  if (!form) return null;

  const valid = FIELDS.every(
    (f) => form[f.key].length <= MAX_FIELD_LEN && ALLOWED_CHARS_RE.test(form[f.key])
  );

  const saveGlobal = async () => {
    if (!valid) { toast.error("Only letters, numbers, spaces, and - . _ & ( ) are allowed."); return; }
    setBusy(true);
    try {
      await updateFacility(DEFAULT_FACILITY_ID, {
        globalSheetImportConfig: {
          titlePrefix: form.titlePrefix.trim(),
          sheetId: form.sheetId.trim(),
          tab: form.tab.trim(),
        },
      });
      toast.success("Global sheet settings saved.");
    } catch (err) {
      console.error("Save global sheet config failed:", err);
      toast.error("Could not save the global sheet settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${color.border.default}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary }}>
          🌐 Global sheet (all three facilities)
        </div>
        <button style={s.primaryBtn} onClick={saveGlobal} disabled={busy}>
          {busy ? "Saving…" : "💾 Save Global"}
        </button>
      </div>
      <p style={{ fontSize: 11, color: color.text.muted, margin: "0 0 10px" }}>
        Used by the Daily List's "Import Global Google Sheet" button AND the
        automated <strong>midnight import</strong>, which resets and re-imports
        Wesley Chapel + Tampa + Carrollwood every night. Separate from the
        facility-specific settings above. Blank fields fall back to the same
        shared defaults.
      </p>
      {/* Assignment-source switch: Google Sheet (default) vs the internal
          scheduling module (/schedule). The midnight import reads this flag —
          flip it only after the internal schedule is verified. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, padding: "8px 10px", borderRadius: 8, border: `1px solid ${color.border.default}`, background: color.surface.raised }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: color.text.primary }}>
            Midnight assignment source:{" "}
            {defaultFacility?.useInternalSchedule ? "📅 Internal schedule" : "📊 Google Sheet"}
          </div>
          <div style={{ fontSize: 11, color: color.text.muted }}>
            {defaultFacility?.useInternalSchedule
              ? "The nightly import reads the in-app Schedule module. The Google Sheet path stays configured below as the fallback."
              : "The nightly import reads the global Google Sheet. Build tomorrow's day in the Schedule module, verify it, then flip."}
          </div>
        </div>
        <button
          role="switch"
          aria-checked={!!defaultFacility?.useInternalSchedule}
          aria-label="Use the internal schedule as the midnight assignment source"
          style={{ ...s.primaryBtn, minWidth: 150, justifyContent: "center", background: defaultFacility?.useInternalSchedule ? "#0e7490" : color.text.muted }}
          disabled={busy}
          onClick={async () => {
            const next = !defaultFacility?.useInternalSchedule;
            try {
              await updateFacility(DEFAULT_FACILITY_ID, { useInternalSchedule: next });
              toast.success(next
                ? "Internal schedule is now the midnight source."
                : "Google Sheet is now the midnight source.");
            } catch (e) {
              console.error("Toggle schedule source failed:", e);
              toast.error("Could not switch the assignment source.");
            }
          }}
        >
          {defaultFacility?.useInternalSchedule ? "Use Google Sheet" : "Use Internal Schedule"}
        </button>
      </div>

      <ImportRunSummary run={lastRun} />

      <div style={s.formGrid}>
        {FIELDS.map((f) => (
          <div key={`g-${f.key}`} style={s.formGroup}>
            <label style={s.label}>{f.label}</label>
            <input
              style={s.input}
              value={form[f.key]}
              disabled={busy}
              placeholder={envFallbackValue(f.envVar, f.placeholder)}
              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Room-family pattern editor (blocker #4) — ADMIN ONLY. Defines which OR-name
// prefixes belong to THIS facility in the shared import sheet. A new (non-
// AdventHealth) campus sets its own pattern here so its rooms parse correctly;
// an unconfigured non-built-in facility imports nothing (rather than falling
// back to Wesley Chapel's rooms). Stored top-level on the facility doc
// (facility.roomFamily) — admin-only write, so no rules change and the AIC
// field-scope stays locked to sheetImportConfig.
const MAX_PATTERN_LEN = 300;
function RoomFamilySection({ facilityId, facility }) {
  const toast = useToast();
  const [pattern, setPattern] = useState(facility?.roomFamily?.pattern || "");
  const [flags, setFlags] = useState(facility?.roomFamily?.flags ?? "i");
  const [sample, setSample] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPattern(facility?.roomFamily?.pattern || "");
    setFlags(facility?.roomFamily?.flags ?? "i");
  }, [facility, facilityId]);

  const trimmed = pattern.trim();
  const compiled = compileRoomFamily(trimmed, flags);
  const patternInvalid = trimmed !== "" && compiled === null;
  const tooLong = pattern.length > MAX_PATTERN_LEN;
  const sampleMatches = compiled && sample.trim() ? compiled.test(sample.trim()) : null;

  const save = async () => {
    if (tooLong) { toast.error(`Pattern must be ${MAX_PATTERN_LEN} characters or fewer.`); return; }
    if (patternInvalid) { toast.error("That is not a valid regular expression."); return; }
    setBusy(true);
    try {
      await updateFacility(facilityId, {
        roomFamily: { pattern: trimmed, flags: (flags || "i").trim() },
      });
      toast.success(trimmed
        ? "Room-family pattern saved."
        : "Room-family pattern cleared — this facility reverts to its built-in default (or matches no rooms if it has none).");
    } catch (err) {
      console.error("Save room-family pattern failed:", err);
      toast.error("Could not save. Administrator rights are required.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${color.border.default}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary }}>
          🚪 Room-family pattern (advanced)
        </div>
        <button style={s.primaryBtn} onClick={save} disabled={busy || patternInvalid || tooLong}>
          {busy ? "Saving…" : "💾 Save Pattern"}
        </button>
      </div>
      <p style={{ fontSize: 11, color: color.text.muted, margin: "0 0 10px" }}>
        A regular expression matched against each OR name in the import sheet to
        decide which rooms belong to <strong>{facilityLabel(facilityId)}</strong>.
        Leave blank to use the built-in default for the AdventHealth campuses. A
        new facility <em>must</em> set this, or its import will match no rooms.
      </p>
      <div style={s.formGroup}>
        <label style={s.label} htmlFor="sheet-pattern">Pattern</label>
        <input id="sheet-pattern"
          style={s.input}
          value={pattern}
          disabled={busy}
          placeholder="e.g. ^(bor|brn|proc)"
          onChange={(e) => setPattern(e.target.value)}
        />
        {patternInvalid && (
          <div role="alert" style={{ fontSize: 11, color: color.accent.error, marginTop: 4 }}>
            Not a valid regular expression.
          </div>
        )}
        {tooLong && (
          <div role="alert" style={{ fontSize: 11, color: color.accent.error, marginTop: 4 }}>
            Pattern is too long (max {MAX_PATTERN_LEN}).
          </div>
        )}
      </div>
      <div style={{ ...s.formGroup, marginTop: 8 }}>
        <label style={s.label} htmlFor="sheet-flags">Flags</label>
        <input id="sheet-flags"
          style={{ ...s.input, maxWidth: 120 }}
          value={flags}
          disabled={busy}
          placeholder="i"
          onChange={(e) => setFlags(e.target.value)}
        />
        <div style={{ fontSize: 11, color: color.text.muted, marginTop: 4 }}>
          Regex flags. Default <code>i</code> (case-insensitive).
        </div>
      </div>
      <div style={{ ...s.formGroup, marginTop: 8 }}>
        <label style={s.label} htmlFor="sheet-sample">Test a room name</label>
        <input id="sheet-sample"
          style={s.input}
          value={sample}
          placeholder="e.g. BOR 5"
          onChange={(e) => setSample(e.target.value)}
        />
        {sample.trim() && compiled && (
          <div style={{ fontSize: 12, marginTop: 4, fontWeight: 700, color: sampleMatches ? color.accent.active : color.accent.error }}>
            {sampleMatches ? "✓ matches — this room would import here" : "✗ no match — this room would NOT import here"}
          </div>
        )}
      </div>
    </div>
  );
}

// AIC-marker editor — ADMIN ONLY. Some campuses name no AIC in the sheet: the
// day's AIC/DOD is whichever attending is printed BOLD or coloured in the
// attending column, and the import reads cell formatting to find them. That was
// a hardcoded facility id in the Cloud Function, so no new campus could use it
// (or knew it existed). Stored top-level on the facility doc (facility.sheetAic)
// — admin-only write, same as roomFamily, so no rules change.
function AicMarkerSection({ facilityId, facility }) {
  const toast = useToast();
  const effective = usesFormattingAic(facilityId, { configByFacility: { [facilityId]: facility } });
  const [on, setOn] = useState(effective);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setOn(usesFormattingAic(facilityId, { configByFacility: { [facilityId]: facility } }));
  }, [facility, facilityId]);

  const save = async (next) => {
    setBusy(true);
    try {
      await updateFacility(facilityId, { sheetAic: { fromFormatting: next === true } });
      setOn(next === true);
      toast.success(next
        ? "This facility's AIC will be read from bold/coloured cells in the sheet."
        : "This facility's AIC will be read from the sheet text, not its formatting.");
    } catch (err) {
      console.error("Save AIC marker failed:", err);
      toast.error("Could not save. Administrator rights are required.");
      // Re-seed from the live doc so the switch never shows an unsaved state.
      setOn(usesFormattingAic(facilityId, { configByFacility: { [facilityId]: facility } }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${color.border.default}` }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary, marginBottom: 6 }}>
        ⭐ AIC marked by cell formatting (advanced)
      </div>
      <p style={{ fontSize: 11, color: color.text.muted, margin: "0 0 10px" }}>
        Turn this on only if <strong>{facilityLabel(facilityId)}</strong>'s sheet marks the
        day's AIC by printing that attending in <strong>bold</strong> or in colour, rather
        than naming them. Leave it off if the AIC is written in the grid — reading
        formatting on a sheet that doesn't use the convention would pick whichever
        attending happens to be styled.
      </p>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={on}
          disabled={busy}
          onChange={(e) => save(e.target.checked)}
        />
        <span>{busy ? "Saving…" : "Read the AIC from bold/coloured attending cells"}</span>
      </label>
    </div>
  );
}

export function SheetImportConfigPanel({ facilityId }) {
  const { isAdmin, isAIC } = useAuth();
  const toast = useToast();
  const { facility, loading } = useFacility(facilityId);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const canEdit = isAdmin || isAIC;

  // Seed the form from the live doc whenever the facility changes.
  useEffect(() => {
    const cfg = facility?.sheetImportConfig || {};
    setForm({
      titlePrefix: cfg.titlePrefix || "",
      sheetId: cfg.sheetId || "",
      tab: cfg.tab || "",
    });
    setErrors({});
  }, [facility, facilityId]);

  if (loading || !form) {
    return <div style={s.panel}><div style={{ fontSize: 13, color: color.text.muted }}>Loading facility…</div></div>;
  }

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const next = {};
    for (const f of FIELDS) {
      const value = form[f.key];
      if (value.length > MAX_FIELD_LEN || !ALLOWED_CHARS_RE.test(value)) {
        next[f.key] = "Only letters, numbers, spaces, and - . _ & ( ) are allowed.";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await updateFacility(facilityId, {
        sheetImportConfig: {
          titlePrefix: form.titlePrefix.trim(),
          sheetId: form.sheetId.trim(),
          tab: form.tab.trim(),
        },
      });
      toast.success("Sheet import settings saved.");
    } catch (err) {
      console.error("Save sheet import config failed:", err);
      toast.error("Could not save. Admin or AIC rights are required for this facility.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const label = facilityLabel(facilityId);
    if (!window.confirm(
      `Reset to default? This clears the custom sheet title, ID, and tab overrides for ${label} and reverts to the shared environment default. Import behavior changes immediately.`
    )) return;
    setSaving(true);
    try {
      await updateFacility(facilityId, {
        sheetImportConfig: { titlePrefix: "", sheetId: "", tab: "" },
      });
      toast.success("Sheet import settings saved.");
    } catch (err) {
      console.error("Reset sheet import config failed:", err);
      toast.error("Could not save. Admin or AIC rights are required for this facility.");
    } finally {
      setSaving(false);
    }
  };

  const saEmail = process.env.REACT_APP_ATC_IMPORT_SA_EMAIL;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(saEmail);
      toast.info("Service account email copied.");
    } catch (err) {
      console.error("Copy service account email failed:", err);
      toast.error("Could not copy. Select and copy the email manually.");
    }
  };

  return (
    <div style={s.panel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={s.panelTitle}>📊 Google Sheet Import</div>
        {canEdit && (
          <button style={s.primaryBtn} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "💾 Save Changes"}
          </button>
        )}
      </div>

      {!canEdit && (
        <p style={{ fontSize: 12, color: color.text.muted, marginTop: 0 }}>
          Only an administrator or the AIC can edit sheet import settings.
        </p>
      )}

      <div style={s.formGrid}>
        {FIELDS.map((f) => {
          const value = form[f.key];
          const error = errors[f.key];
          const helper = value.trim()
            ? null
            : (f.blankHelper || `Not yet customized — using the shared default: ${envFallbackValue(f.envVar, f.envFallback)}.`);
          return (
            <div key={f.key} style={s.formGroup}>
              <label style={s.label}>{f.label}</label>
              <input
                style={s.input}
                value={value}
                disabled={!canEdit || saving}
                placeholder={envFallbackValue(f.envVar, f.placeholder)}
                onChange={(e) => setField(f.key, e.target.value)}
              />
              {error && (
                <div role="alert" style={{ fontSize: 11, color: color.accent.error, marginTop: 4 }}>
                  {error}
                </div>
              )}
              {!error && helper && (
                <div style={{ fontSize: 11, color: color.text.muted, marginTop: 4 }}>
                  {helper}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div style={{ marginTop: 16 }}>
          <button style={s.ghostBtn} onClick={reset} disabled={saving}>
            Reset to Facility Default
          </button>
        </div>
      )}

      {/* GOOGLE SHEET ENGINE — the ON/OFF switch for the live daytime sync,
          admin-only and GLOBAL (it writes facilities/default like the global
          config below, whichever facility is selected above). Placed FIRST of
          the advanced sections deliberately: it is the only control here that
          decides whether an unattended process may write to a live board, and
          it carries the engine's liveness beside it so "ON" is never read as
          "running". Own file — this one is already past the 500-line cap.
          Contract: src/domain/sheetEngine.js. */}
      {isAdmin && <SheetEngineSection />}

      {/* GLOBAL sheet config — a SEPARATE sheet used by the Daily List's
          "Import Global Google Sheet" button (writes ALL THREE facilities at
          once). Stored on facilities/default.globalSheetImportConfig; admin
          writes any facility field, so no extra rules. Admin-only editor. */}
      {isAdmin && <GlobalConfigSection saving={saving} />}

      {/* Room-family pattern — admin-only; defines which sheet OR names belong
          to THIS facility (blocker #4). Admin writes any facility field, so no
          rules change. */}
      {isAdmin && <RoomFamilySection facilityId={facilityId} facility={facility} />}
      {isAdmin && <AicMarkerSection facilityId={facilityId} facility={facility} />}

      <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${color.border.default}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary, marginBottom: 8 }}>
          Service account
        </div>
        {saEmail ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: color.text.primary }}>{saEmail}</span>
            <button
              aria-label="Copy service account email"
              onClick={copyEmail}
              style={{
                background: "transparent",
                border: `1px solid ${color.border.default}`,
                color: color.text.secondary,
                cursor: "pointer",
                fontSize: 14,
                borderRadius: 6,
                width: 44,
                height: 44,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              📋
            </button>
            <p style={{ fontSize: 12, color: color.text.muted, margin: 0, flexBasis: "100%" }}>
              Share the daily sheet with this address once.
            </p>
          </div>
        ) : (
          <p style={{ fontSize: 12, color: color.text.muted, margin: 0 }}>
            Service account email not available yet — check that the scheduled import function is deployed.
          </p>
        )}
      </div>
    </div>
  );
}
